import React, { useState, useEffect, useMemo } from 'react';
import { func, shape, number, string } from 'prop-types';
import { makeStyles, TextField } from '@material-ui/core';
import classnames from 'classnames';
import { Button, AlertBanner } from '../../../common/components';
import { useContent } from '../../../content/content-provider';
import { withConfig } from '../../../common/providers/configProvider';
import { errorCodes } from '../../../common/utils/error-codes';
import validate from './GiftCardValidate';

const styles = makeStyles(({ palette, spacing }: any) => ({
  footer: {
    left: '0px',
    right: '0px',
    bottom: '0px',
    position: 'fixed',
    padding: `${spacing(6)}px ${spacing(5)}px ${spacing(7.5)}px ${spacing(5)}px`,
    backgroundColor: palette.background.default,
    boxShadow: `0px -1px 5px ${palette.background.contrastShadow}`,
    borderRadius: '10px 10px 0px 0px',
    zIndex: 2,
  },
  textField: {
    marginTop: spacing(6),
  },
  errorBanner: {
    marginTop: spacing(6),
  },
}));

const getMapValidationErrorToContent = (content, giftCardMinNumber, giftCardMaxNumber) => () =>
  content.giftCard.errors.cardNumber
    .replace('{min}', giftCardMinNumber)
    .replace('{max}', giftCardMaxNumber);

const getMapErrorCodeToContent = (content) => (errorCode) =>
  content.giftCard.errors[errorCodes[errorCode].text];

const GiftCardCheckBalance = ({
  config: {
    content: {
      giftCards: { giftCardMinNumber, giftCardMaxNumber },
    },
  },
  getGiftCardBalance,
  giftCardError,
  giftCardErrorTime,
}) => {
  const [content] = useContent();
  const classes = styles();
  const [cardNumber, setCardNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isServerSideValidation, setIsServerSideValidation] = useState(false);

  const { error: validationError } = useMemo(
    () => validate(cardNumber, { max: giftCardMaxNumber }),
    // eslint-disable-next-line no-autofix/react-hooks/exhaustive-deps
    [cardNumber]
  );

  const isNotAllowed =
    giftCardError === errorCodes.MAXIMUM_GIFT_CARD_ATTEMPTS.error ||
    giftCardError === errorCodes.GIFT_CARD_SERVICE_DOWN.error;

  const errorClass = classnames({
    [classes.errorBanner]: isNotAllowed,
  });

  useEffect(() => {
    setIsServerSideValidation(false);
  }, [cardNumber]);

  useEffect(() => {
    if (giftCardError) {
      setIsServerSideValidation(true);
      setIsLoading(false);
    }
  }, [giftCardError, giftCardErrorTime]);

  const mapValidationErrorToContent = getMapValidationErrorToContent(
    content,
    giftCardMinNumber,
    giftCardMaxNumber
  );
  const mapErrorCodeToContent = getMapErrorCodeToContent(content);

  const errorCode =
    (isNotAllowed && errorCodes[giftCardError]) ||
    (validationError && errorCodes[validationError]) ||
    '';

  const errorText =
    (validationError && mapValidationErrorToContent()) ||
    (giftCardError && isServerSideValidation && mapErrorCodeToContent(giftCardError)) ||
    '';

  const isDisabled = !!(cardNumber.length < giftCardMinNumber || errorCode);

  const handleInput = (input) => {
    const { value } = input.target;
    setCardNumber(value);
  };

  const handleClick = () => {
    getGiftCardBalance(cardNumber);
    setIsLoading(true);
  };

  return (
    <>
      <TextField
        id="check-balance"
        fullWidth
        label={content.giftCard.label}
        inputProps={{ inputMode: 'numeric' }}
        onChange={handleInput}
        error={!!errorText}
        disabled={isNotAllowed}
        className={classes.textField}
      />
      {!!errorText && <AlertBanner className={errorClass}>{errorText}</AlertBanner>}
      {!isNotAllowed && (
        <div className={classes.footer}>
          <Button onClick={handleClick} isDisabled={isDisabled} isLoading={isLoading}>
            {content.giftCard.checkBalanceButton}
          </Button>
        </div>
      )}
    </>
  );
};

GiftCardCheckBalance.propTypes = {
  config: shape({
    content: shape({
      giftCards: shape({
        giftCardMinNumber: number.isRequired,
        giftCardMaxNumber: number.isRequired,
      }),
    }),
  }).isRequired,
  getGiftCardBalance: func.isRequired,
  giftCardError: string,
  giftCardErrorTime: string,
};

GiftCardCheckBalance.defaultProps = {
  giftCardError: null,
  giftCardErrorTime: null,
};

export default withConfig(GiftCardCheckBalance);
